import React from "react";
import { getSupabaseClient } from "../../data/supabase";
export default function GuestMfaSettings() {
  const sb = getSupabaseClient();
  const [factor, setFactor] = React.useState("");
  const [enrollment, setEnrollment] = React.useState<{
    id: string;
    qr: string;
    secret: string;
  } | null>(null);
  const [code, setCode] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [invalid, setInvalid] = React.useState("");
  const focusInvalid = (id: string) => {
    setInvalid(id);
    document.getElementById(id)?.focus();
  };
  const [loaded, setLoaded] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [removing, setRemoving] = React.useState(false);
  async function load() {
    const { data, error } = await sb.auth.mfa.listFactors();
    if (error) {
      setMessage("Authenticator settings could not be loaded. Try again.");
      return;
    }
    setFactor(data.totp[0]?.id || "");
    setLoaded(true);
  }
  React.useEffect(() => {
    void load();
  }, []);
  async function begin() {
    setBusy(true);
    setMessage("");
    try {
      const { data: existing, error: listError } =
        await sb.auth.mfa.listFactors();
      if (listError) throw listError;
      for (const pending of existing.all.filter(
        (f) => f.factor_type === "totp" && f.status === "unverified",
      )) {
        const { error } = await sb.auth.mfa.unenroll({ factorId: pending.id });
        if (error) throw error;
      }
      const { data, error } = await sb.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: "Portal authenticator",
        issuer: "ITA Portal",
      });
      if (error) throw error;
      setEnrollment({
        id: data.id,
        qr: data.totp.qr_code,
        secret: data.totp.secret,
      });
      setCode("");
    } catch (error) {
      setMessage(
        (error as { code?: string }).code === "mfa_totp_enroll_not_enabled"
          ? "Authenticator setup is not available yet. Ask your ITA coordinator to enable authenticator support. You can still sign in with your password."
          : "Authenticator setup could not be started. Try again.",
      );
    } finally {
      setBusy(false);
    }
  }
  async function verify(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setMessage("");
    if (!/^\d{6}$/.test(code)) {
      setMessage("Enter the six-digit code from your authenticator app.");
      focusInvalid("authenticator-code");
      return;
    }
    setBusy(true);
    try {
      const { error } = await sb.auth.mfa.challengeAndVerify({
        factorId: enrollment?.id || factor,
        code,
      });
      if (error) throw error;
      if (removing) {
        const { error: removeError } = await sb.auth.mfa.unenroll({
          factorId: factor,
        });
        if (removeError) throw removeError;
        await sb.auth.refreshSession();
        setMessage("Authenticator removed. You can set up another app.");
      } else
        setMessage(
          "Authenticator enabled. Future guest sign-ins will ask for a code.",
        );
      setEnrollment(null);
      setRemoving(false);
      setCode("");
      await load();
    } catch {
      setMessage(
        "That code could not be verified. Use the current code and try again.",
      );
    } finally {
      setBusy(false);
    }
  }
  async function cancel() {
    setBusy(true);
    if (enrollment) {
      const { error } = await sb.auth.mfa.unenroll({ factorId: enrollment.id });
      if (error) {
        setMessage("Setup could not be cancelled. Try again.");
        setBusy(false);
        return;
      }
    }
    setEnrollment(null);
    setRemoving(false);
    setCode("");
    setMessage("");
    setBusy(false);
  }
  return (
    <section
      className="stack gap-sm"
      aria-labelledby="guest-authenticator-heading"
    >
      <h3 id="guest-authenticator-heading">Authenticator app</h3>
      <p>
        Add a free second step to your guest account. Use an app such as
        Microsoft Authenticator or Google Authenticator. If you lose access to
        the app, contact your ITA coordinator.
      </p>
      {!loaded ? (
        <button className="btn" type="button" onClick={() => void load()}>
          Load authenticator settings
        </button>
      ) : !enrollment && !removing ? (
        <div>
          <p>
            {factor
              ? "Authenticator is enabled."
              : "Authenticator is not enabled."}
          </p>
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() =>
              factor
                ? (setRemoving(true), setCode(""), setMessage(""))
                : void begin()
            }
          >
            {factor ? "Remove authenticator" : "Set up authenticator"}
          </button>
        </div>
      ) : (
        <>
          {enrollment && (
            <>
              <p>
                Scan this code in your authenticator app, then enter its current
                six-digit code.
              </p>
              <img
                src={enrollment.qr}
                width="220"
                height="220"
                alt="QR code for your Portal authenticator"
              />
              <details>
                <summary>Enter a setup key instead</summary>
                <code style={{ overflowWrap: "anywhere" }}>
                  {enrollment.secret}
                </code>
              </details>
            </>
          )}
          {removing && <p>Enter a current code to confirm removal.</p>}
          <form noValidate onSubmit={verify} className="stack gap-sm">
            <label className="settings-field">
              <span>Authenticator code</span>
              <input
                id="authenticator-code"
                aria-invalid={invalid === "authenticator-code"}
                className="input"
                style={{ maxWidth: 260 }}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                disabled={busy}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                aria-describedby="authenticator-status"
                autoFocus
              />
            </label>
            <div className="row gap-sm">
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy
                  ? "Verifying…"
                  : removing
                    ? "Confirm removal"
                    : "Enable authenticator"}
              </button>
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() => void cancel()}
              >
                Cancel
              </button>
            </div>
          </form>
        </>
      )}
      <p id="authenticator-status" role="status" aria-live="polite">
        {message}
      </p>
    </section>
  );
}
